import type { SavedRoofModulesLayout } from '../../../../../@types/types';
import {
    addModulesToAllRoofsMaxFill,
    removeGreyModulesAndSingleModuleRoofs,
    SetModuleGraph,
} from '../../Three-JS-Viewer/Controllers/ModuleControl';
import { captureLiveModulesLayout } from '../../Three-JS-Viewer/Controllers/Common';
import {
    clearAllModuleMeshes,
    restoreModulesLayoutFromSnapshot,
} from '../../Three-JS-Viewer/Controllers/DefaultLayoutControl';
import {
    getCategoryMainLayoutKey,
    normalizeLayoutMinEff,
    resolveCategoryApplyLayoutParams,
    type CategoryLayoutCandidate,
} from './categoryBestVariantScoring';
import { resolveAiLayoutRoofFaceIds } from './resolveAiLayoutRoofFaceIds';
import {
    countModulesInLayout,
    isSnapshotConsistentWithCard,
    logAiDesignResolve,
    readExpectedCardMetrics,
    readLiveViewerMetrics,
} from './aiDesignFlowDebug';
import {
    resolveModulesLayoutMatchingCount,
} from './aiLayoutCacheHydration';
import { resolveAltLayoutVariantLayoutKey } from './filterAltLayoutDisplayVariants';
import { ONE_DRAW_DEFAULT_MAXFILL_CATEGORY } from '../../../../../services/layoutVariantPrompts';
import {
    countModulesApplicableInRemappedLayout,
    remapModulesLayoutToViewerWithMap,
    remapRoofFaceIdsToViewer,
} from './remapModulesLayoutRoofFaceIds';
import { useViewerStore } from '../../../../../store/store';

type LayoutRoofSummary = { roofFaceId?: string; roofNum?: unknown };

function readLayoutRoofSummariesForCategory(
    categoryLabel: string | undefined,
    layoutKey: string,
): LayoutRoofSummary[] | undefined {
    const state = useViewerStore.getState() as any;
    const summariesByKey = (state.layoutRoofSummariesByKey ?? {}) as Record<string, LayoutRoofSummary[]>;
    const catKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : undefined;
    const fromSummaries =
        (catKey ? summariesByKey[catKey] : undefined) ??
        (categoryLabel ? summariesByKey[categoryLabel] : undefined) ??
        summariesByKey[layoutKey];
    if (fromSummaries?.length) return fromSummaries;

    const shadeWeights = (state.aiModuleFillingScenarios?.[0]?.shadeWeights ?? []) as Array<{
        roofFaceId?: string;
        roofNum?: unknown;
    }>;
    const fromShade = shadeWeights
        .map((row) => ({
            roofFaceId: typeof row.roofFaceId === 'string' ? row.roofFaceId : undefined,
            roofNum: row.roofNum,
        }))
        .filter((row) => row.roofFaceId);
    return fromShade.length ? fromShade : undefined;
}

/** Catalog Maxfill is stored as layout key `default`; open-3D may omit the category label. */
export function resolveApplyCategoryLabel(layoutKey: string, categoryLabel?: string): string | undefined {
    const trimmed = categoryLabel?.trim();
    if (trimmed) return trimmed;
    if (layoutKey === 'default') return ONE_DRAW_DEFAULT_MAXFILL_CATEGORY;
    return undefined;
}

/** Module count from a snapshot rebound onto roofs present in the live viewer. */
export function countModulesApplicableInViewer(
    viewer: { roofMeshArr?: Array<{ roofFaceId?: string }> } | null | undefined,
    modulesLayout: SavedRoofModulesLayout | null | undefined,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): number {
    if (!viewer?.roofMeshArr?.length || !Array.isArray(modulesLayout) || !modulesLayout.length) {
        return 0;
    }
    const remapped = remapModulesLayoutToViewerWithMap(viewer, modulesLayout, layoutRoofSummaries).layout;
    return countModulesApplicableInRemappedLayout(remapped);
}

/**
 * Whether a cached placement snapshot can be applied in the live viewer after roof-id remapping.
 */
export function isSnapshotApplicableToViewer(
    viewer: { roofMeshArr?: Array<{ roofFaceId?: string }> } | null | undefined,
    layoutKey: string,
    categoryLabel: string | undefined,
    modulesLayout: SavedRoofModulesLayout | null | undefined,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): boolean {
    if (!isSnapshotConsistentWithCard(layoutKey, categoryLabel, modulesLayout)) return false;
    const summaries =
        layoutRoofSummaries ?? readLayoutRoofSummariesForCategory(categoryLabel, layoutKey);
    const applicable = countModulesApplicableInViewer(viewer, modulesLayout, summaries);
    if (applicable <= 0) return false;
    const cardModules = countModulesInLayout(modulesLayout);
    return applicable === cardModules;
}

export type ApplyCategoryLayoutInViewerArgs = {
    viewer: any;
    layoutKey: string;
    categoryLabel?: string;
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    layoutCandidatesByCategory?: Record<
        string,
        Array<{
            key?: string;
            roofFaceIds?: string[];
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            modulesLayout?: SavedRoofModulesLayout;
        }>
    >;
    variantsByCategory?: Record<string, unknown[]>;
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>;
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>;
    bestRoofsForLayout: { roofFaceIds?: string[] };
    defaultMaxfillRoofIds?: string[];
    simulateLayoutForRoofIds: (
        roofFaceIds: string[],
        opts?: {
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            layoutCacheKey?: string;
        },
    ) => Promise<{ modulesLayout?: SavedRoofModulesLayout } | void>;
    applyRoofSetInViewer: (
        roofFaceIds: string[],
        opts?: {
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            deferShadeCalc?: boolean;
        },
    ) => Promise<void>;
};

/**
 * Apply a catalog / cached AI category layout to the live 3D viewer.
 * Tries exact placement snapshots first, then re-simulates the roof set (same as Run AI),
 * with max-fill only as a last resort when roof ids cannot be resolved.
 */
export async function applyCategoryLayoutInViewer(
    args: ApplyCategoryLayoutInViewerArgs,
): Promise<SavedRoofModulesLayout | undefined> {
    const {
        viewer,
        layoutKey,
        categoryLabel,
        categoryMainLayoutByCategory = {},
        layoutCandidatesByCategory = {},
        variantsByCategory = {},
        layoutModulesLayoutByKey = {},
        roofCombinationsFromAi,
        bestRoofsForLayout,
        defaultMaxfillRoofIds = [],
        simulateLayoutForRoofIds,
        applyRoofSetInViewer,
    } = args;

    if (!viewer) return undefined;

    const effectiveCategoryLabel = resolveApplyCategoryLabel(layoutKey, categoryLabel);

    const categoryApply = resolveCategoryApplyLayoutParams(
        effectiveCategoryLabel,
        categoryMainLayoutByCategory,
        layoutCandidatesByCategory,
        variantsByCategory,
        layoutModulesLayoutByKey,
    );

    const main = effectiveCategoryLabel ? categoryMainLayoutByCategory?.[effectiveCategoryLabel] : undefined;
    const expectedCardModules = readExpectedCardMetrics(layoutKey, effectiveCategoryLabel).modules;
    const expectedModules =
        (expectedCardModules != null && expectedCardModules > 0 ? expectedCardModules : undefined) ??
        (main?.modules && main.modules > 0 ? main.modules : undefined);
    const catKey = effectiveCategoryLabel ? getCategoryMainLayoutKey(effectiveCategoryLabel) : undefined;
    const variantKey =
        effectiveCategoryLabel && main?.variantId
            ? resolveAltLayoutVariantLayoutKey(effectiveCategoryLabel, { id: main.variantId })
            : undefined;
    const modulesLayout = resolveModulesLayoutMatchingCount(
        layoutModulesLayoutByKey,
        [catKey, main?.key, effectiveCategoryLabel, layoutKey, variantKey, 'default'],
        expectedModules,
        categoryApply?.modulesLayout,
    );
    const layoutRoofSummaries = readLayoutRoofSummariesForCategory(effectiveCategoryLabel, layoutKey);
    const remappedModulesLayout = modulesLayout?.length
        ? remapModulesLayoutToViewerWithMap(viewer, modulesLayout, layoutRoofSummaries).layout
        : undefined;

    if (
        remappedModulesLayout?.length &&
        isSnapshotApplicableToViewer(
            viewer,
            layoutKey,
            effectiveCategoryLabel,
            modulesLayout,
            layoutRoofSummaries,
        )
    ) {
        logAiDesignResolve({
            branch: 'modules-layout-snapshot',
            modulesLayout: remappedModulesLayout,
            roofFaceIds: categoryApply?.roofFaceIds,
            dir: categoryApply?.dir ?? null,
        });
        await restoreModulesLayoutFromSnapshot(viewer, remappedModulesLayout, { skipShadeRefresh: true });
        if (readLiveViewerMetrics().modules > 0) {
            return remappedModulesLayout;
        }
        console.warn(
            '[AI-DESIGN-FLOW] placement snapshot matched the card but placed 0 modules — re-simulating roof set',
        );
    } else if (modulesLayout?.length) {
        console.warn(
            '[AI-DESIGN-FLOW] ignoring unusable placement snapshot — re-simulating roof set',
            {
                cardConsistent: isSnapshotConsistentWithCard(layoutKey, effectiveCategoryLabel, modulesLayout),
                applicableModules: countModulesApplicableInViewer(
                    viewer,
                    modulesLayout,
                    layoutRoofSummaries,
                ),
                snapshotModules: countModulesInLayout(modulesLayout),
                remappedModules: countModulesApplicableInRemappedLayout(remappedModulesLayout),
            },
        );
    }

    const isDefaultMaxfill =
        layoutKey === 'default' || effectiveCategoryLabel === ONE_DRAW_DEFAULT_MAXFILL_CATEGORY;
    if (isDefaultMaxfill) {
        const dir = categoryApply?.dir ?? null;
        logAiDesignResolve({
            branch: 'default-maxfill',
            dir,
            roofFaceIds: categoryApply?.roofFaceIds,
        });
        clearAllModuleMeshes(viewer);
        addModulesToAllRoofsMaxFill(viewer, null, dir as any);
        await SetModuleGraph(viewer);
        const maxfillRoofIds = (viewer.roofMeshArr ?? [])
            .map((r: { roofFaceId?: string }) => r.roofFaceId)
            .filter(Boolean);
        if (maxfillRoofIds.length) {
            removeGreyModulesAndSingleModuleRoofs(viewer, maxfillRoofIds);
            await SetModuleGraph(viewer);
        }
        const liveLayout = captureLiveModulesLayout(viewer);
        return Array.isArray(liveLayout) && liveLayout.length ? liveLayout : undefined;
    }

    let roofFaceIds = categoryApply?.roofFaceIds?.length ? categoryApply.roofFaceIds : [];
    const dir = categoryApply?.dir ?? null;
    const minEff = categoryApply?.minEff ?? null;

    roofFaceIds = remapRoofFaceIdsToViewer(viewer, roofFaceIds, modulesLayout, layoutRoofSummaries);

    if (!roofFaceIds.length) {
        roofFaceIds = remapRoofFaceIdsToViewer(
            viewer,
            resolveAiLayoutRoofFaceIds({
                layoutKey,
                categoryLabel: effectiveCategoryLabel,
                roofCombinationsFromAi,
                layoutCandidatesByCategory: layoutCandidatesByCategory as Record<
                    string,
                    Array<{ key: string; roofFaceIds?: string[] }>
                >,
                bestRoofsForLayout,
                defaultMaxfillRoofIds,
            }),
            modulesLayout,
            layoutRoofSummaries,
        );
    }

    if (roofFaceIds.length > 0) {
        const layoutCacheKey = effectiveCategoryLabel ? getCategoryMainLayoutKey(effectiveCategoryLabel) : layoutKey;
        logAiDesignResolve({
            branch: 'simulate-roof-set',
            roofFaceIds,
            dir,
        });
        const simulated = await simulateLayoutForRoofIds(roofFaceIds, {
            dir,
            minEff: normalizeLayoutMinEff(minEff),
            layoutCacheKey,
        });
        const simulatedLayout = simulated?.modulesLayout;
        if (readLiveViewerMetrics().modules > 0) {
            return simulatedLayout?.length ? simulatedLayout : undefined;
        }

        logAiDesignResolve({
            branch: 'roof-set-fill',
            roofFaceIds,
            dir,
        });
        await applyRoofSetInViewer(roofFaceIds, {
            dir,
            minEff: normalizeLayoutMinEff(minEff),
            deferShadeCalc: true,
        });
        if (readLiveViewerMetrics().modules > 0) {
            return simulatedLayout?.length ? simulatedLayout : undefined;
        }
    }

    logAiDesignResolve({ branch: 'maxfill-fallback' });
    console.warn(
        '[AI-DESIGN-FLOW] could not resolve a specific layout for',
        effectiveCategoryLabel ?? layoutKey,
        '— falling back to default max-fill.',
    );
    clearAllModuleMeshes(viewer);
    addModulesToAllRoofsMaxFill(viewer, null, dir as any);
    await SetModuleGraph(viewer);
    const fallbackRoofIds = roofFaceIds.length
        ? roofFaceIds
        : (viewer.roofMeshArr ?? []).map((r: { roofFaceId?: string }) => r.roofFaceId).filter(Boolean);
    if (fallbackRoofIds.length) {
        removeGreyModulesAndSingleModuleRoofs(
            viewer,
            fallbackRoofIds,
            normalizeLayoutMinEff(minEff) ?? undefined,
        );
        await SetModuleGraph(viewer);
    }
    return undefined;
}
